import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { stockAdjustmentsApi, warehousesApi, inventoryItemsApi } from '../../api/warehouse';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';

export const STOCK_ADJUSTMENT_STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected', posted: 'Posted' };
const REASON_LABELS: Record<string, string> = {
  physical_count_variance: 'Physical Count Variance', damage: 'Damage', expiry: 'Expiry', loss: 'Loss',
  data_correction: 'Data Correction', return: 'Return', write_off: 'Write-Off', other: 'Other',
};

interface CreateAdjustmentForm {
  warehouseId: number; itemId: number; currentQuantity: number; adjustmentQuantity: number; reason: string; justification: string;
}

export function StockAdjustmentsListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateAdjustmentForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['stock-adjustments'], queryFn: () => stockAdjustmentsApi.list() });
  const { data: warehousesData } = useQuery({ queryKey: ['warehouses'], queryFn: () => warehousesApi.list() });
  const warehouses = warehousesData?.data ?? [];
  const { data: inventoryItemsData } = useQuery({ queryKey: ['inventory-items'], queryFn: () => inventoryItemsApi.list() });
  const inventoryItems = inventoryItemsData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (input: CreateAdjustmentForm) => stockAdjustmentsApi.create({ ...input, warehouseId: Number(input.warehouseId), itemId: Number(input.itemId), currentQuantity: Number(input.currentQuantity), adjustmentQuantity: Number(input.adjustmentQuantity) }),
    onSuccess: (result) => {
      showSuccess(`Adjustment ${result.data.adjustmentReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/stock-adjustments/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const adjustments = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Stock Adjustments" description="Request a correction to recorded stock quantity, with a documented reason. Every adjustment requires approval from someone other than the requester." />

      <SectionHeader title="Adjustments" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Adjustment'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Warehouse
            <select {...register('warehouseId', { required: true, valueAsNumber: true })}>
              <option value="">Select…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label>Item
            <select {...register('itemId', { required: true, valueAsNumber: true })}>
              <option value="">Select…</option>
              {inventoryItems.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
            </select>
          </label>
          <label>Current Quantity (System) <input type="number" step="0.01" {...register('currentQuantity', { required: true, valueAsNumber: true })} /></label>
          <label>Adjustment Quantity (+/-) <input type="number" step="0.01" placeholder="e.g. -5 or 10" {...register('adjustmentQuantity', { required: true, valueAsNumber: true })} /></label>
          <label>Reason
            <select {...register('reason', { required: true })}>
              {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Justification <input {...register('justification', { required: true })} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create Draft</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load stock adjustments. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={adjustments}
          rowKey={(a) => a.id}
          columns={[
            { header: 'Reference', render: (a) => <a onClick={() => navigate(`/logistics/stock-adjustments/${a.id}`)} className="table-link">{a.adjustment_reference}</a> },
            { header: 'Item', render: (a) => a.item_name },
            { header: 'Warehouse', render: (a) => a.warehouse_name },
            { header: 'Adjustment', render: (a) => a.adjustment_quantity },
            { header: 'Reason', render: (a) => REASON_LABELS[a.reason] ?? a.reason },
            { header: 'Status', render: (a) => <StatusBadge status={a.status} label={STOCK_ADJUSTMENT_STATUS_LABELS[a.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
